import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Film, ChevronDown, ChevronUp } from 'lucide-react';
import { peopleApi, languageToTMDB } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const StarCard = ({ star, compact = false }) => {
  const { language } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDetails = async () => {
    if (details) {
      setExpanded(!expanded);
      return;
    }
    
    try {
      setLoading(true);
      const tmdbLang = languageToTMDB[language] || 'en-US';
      const data = await peopleApi.getDetail(star.id, tmdbLang);
      setDetails(data);
      setExpanded(true);
    } catch (error) {
      console.error('Error fetching star details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Truncate biography
  const truncateBio = (bio, maxLength = 150) => {
    if (!bio) return '';
    if (bio.length <= maxLength) return bio;
    return bio.substring(0, maxLength).trim() + '...';
  };

  if (compact) {
    // Compact version for homepage - clean card without movie posters
    return (
      <Link 
        to={`/stars/${star.id}`}
        className="group block"
      >
        <div className="relative">
          <div className="aspect-[2/3] overflow-hidden rounded-lg bg-[#1a2420]">
            {star.profile_path ? (
              <img
                src={star.profile_path}
                alt={star.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-16 h-16 text-[#52ea88]/30" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-transparent to-transparent" />
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-lg font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors">
              {star.name}
            </h3>
            <p className="text-sm text-[#52ea88]">
              {star.known_for_department}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  // Full version for stars page
  return (
    <div className="bg-[#0d1411] border border-[#52ea88]/10 rounded-lg overflow-hidden hover:border-[#52ea88]/30 transition-colors">
      <div className="flex flex-col sm:flex-row">
        {/* Profile Image */}
        <div className="sm:w-48 flex-shrink-0">
          <Link to={`/stars/${star.id}`}>
            <div className="aspect-[2/3] sm:aspect-auto sm:h-full bg-[#1a2420]">
              {star.profile_path ? (
                <img
                  src={star.profile_path}
                  alt={star.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full min-h-[200px] flex items-center justify-center">
                  <User className="w-16 h-16 text-[#52ea88]/30" />
                </div>
              )}
            </div>
          </Link>
        </div>
        
        {/* Info */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Link to={`/stars/${star.id}`}>
                <h3 className="text-xl font-semibold text-[#E6FFF6] hover:text-[#52ea88] transition-colors">
                  {star.name}
                </h3>
              </Link>
              <p className="text-sm text-[#52ea88] mt-1">
                {star.known_for_department}
              </p>
            </div>
          </div>
          
          {/* Known for movies */}
          {star.known_for && star.known_for.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-[#9FBFB4] mb-2">Known for:</p>
              <div className="flex gap-2 flex-wrap">
                {star.known_for.map((movie) => (
                  <span 
                    key={movie.id}
                    className="text-xs text-[#E6FFF6] bg-[#1a2420] px-2 py-1 rounded"
                  >
                    {movie.title}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Expand button */}
          <button
            onClick={fetchDetails}
            disabled={loading}
            className="mt-4 flex items-center gap-2 text-sm text-[#52ea88] hover:text-[#E6FFF6] transition-colors"
          >
            {loading ? (
              'Loading...'
            ) : expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show bio & recent movies
              </>
            )}
          </button>
          
          {/* Expanded details */}
          {expanded && details && (
            <div className="mt-4 pt-4 border-t border-[#52ea88]/10">
              {/* Biography */}
              {details.biography && (
                <p className="text-sm text-[#9FBFB4] leading-relaxed mb-4">
                  {truncateBio(details.biography, 300)}
                </p>
              )}
              
              {/* Recent movies */}
              {details.recent_movies && details.recent_movies.length > 0 && (
                <div>
                  <p className="text-xs text-[#9FBFB4] mb-3">Recent/Popular Movies:</p>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {details.recent_movies.map((movie) => (
                      <Link
                        key={movie.id}
                        to={`/movie/${movie.id}`}
                        className="flex-shrink-0 w-20 group"
                      >
                        <div className="aspect-[2/3] rounded overflow-hidden bg-[#1a2420]">
                          {movie.poster ? (
                            <img
                              src={movie.poster}
                              alt={movie.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-6 h-6 text-[#52ea88]/30" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-[#E6FFF6] mt-1 line-clamp-2 group-hover:text-[#52ea88] transition-colors">
                          {movie.title}
                        </p>
                        {movie.year && (
                          <p className="text-xs text-[#9FBFB4]">{movie.year}</p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StarCard;
